// Compares a contacted issue's live supply-chain state against the
// contact_snapshot captured at contact time. Returns a list of human-
// readable changes; empty list means no drift. Only rows with an existing
// snapshot can drift — pre-migration contacted rows won't produce signal
// until they're re-contacted.

function dateEquals(a, b) {
  // Normalize null/undefined/"" to the same bucket so we don't report
  // drift on cosmetic differences.
  const na = a == null || a === "" ? null : a;
  const nb = b == null || b === "" ? null : b;
  return na === nb;
}

export function computeIssueDrift(issue) {
  const snap = issue?.contact_snapshot;
  if (!snap) return { drifted: false, changes: [] };

  const changes = [];
  const snapDates = snap.oos_size_clear_dates || {};
  const liveDates = issue.oos_size_clear_dates || {};
  const snapSizes = new Set(snap.oos_sizes || []);
  const liveSizes = new Set(issue.oos_sizes || []);

  // Walk sizes that were OOS at decision time. Each can be in one of
  // two states now:
  //   - Still OOS (in both sets) → compare clear_date for drift.
  //   - Recovered (only in snap)  → surface as size_recovered.
  // Sizes only in `live` are new OOS (scope grew) — those are handled
  // upstream by the scanner (contacted flips to new; dismissed is
  // retired + re-inserted), so they don't appear on a visible
  // drifted row.
  for (const size of snapSizes) {
    if (!liveSizes.has(size)) {
      changes.push({ type: "size_recovered", size });
      continue;
    }
    const from = snapDates[size] ?? null;
    const to = liveDates[size] ?? null;
    if (!dateEquals(from, to)) {
      changes.push({ type: "clear_date", size, from, to });
    }
  }

  // Severity drift — warning ⇄ critical. Included in the data for
  // completeness; describeChange() suppresses it on the customer-facing
  // output because the per-size lines already carry the story.
  if (snap.severity !== issue.severity) {
    changes.push({
      type: "severity",
      from: snap.severity,
      to: issue.severity,
    });
  }

  // A row counts as "drifted" only if at least one change renders to a
  // user-facing line. Severity-only drift (no matching clear_date or
  // size recovery) would produce an empty diff block + a chip with
  // nothing to explain — worse than staying hidden. In practice severity
  // always changes alongside a clear_date (incoming appeared/vanished),
  // so this only prunes synthetic cases and bad data.
  const renderable = changes.some((c) => describeChange(c) !== null);
  return { drifted: renderable, changes };
}

// Project-level rollup used by the card's Updated chip. True iff any
// issue in the project's issues array has drift. Non-contacted issues
// have no snapshot and contribute nothing.
export function projectHasDrift(project) {
  return (project?.issues || []).some(
    (i) => computeIssueDrift(i).drifted,
  );
}

// Drift-block formatters — shared between the dashboard detail sheet
// and the PDF export. Wording is customer-facing because the PDF ships
// to customers; reps read the same lines on the dashboard and it's at
// least as clear as the engineering-voice "severity warning → critical"
// it replaced. Severity changes are intentionally omitted — per-size
// clear-date lines already carry that signal.

export function formatDriftDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const mmm = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const thisYear = new Date().getFullYear();
  return year === thisYear ? `${mmm} ${day}` : `${mmm} ${day}, ${year}`;
}

export function formatContactedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const mmm = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const thisYear = new Date().getFullYear();
  return year === thisYear ? `${mmm} ${day}` : `${mmm} ${day}, ${year}`;
}

export function describeChange(change) {
  if (change.type === "clear_date") {
    const from = formatDriftDate(change.from);
    const to = formatDriftDate(change.to);
    if (from && to) {
      return `${change.size} · expected ${to} (was ${from})`;
    }
    if (!from && to) {
      return `${change.size} · now expected ${to}`;
    }
    if (from && !to) {
      return `${change.size} · no incoming stock (was expected ${from})`;
    }
    return null;
  }
  if (change.type === "size_recovered") {
    return `${change.size} · back in stock`;
  }
  // Severity changes intentionally omitted — redundant with clear-date lines.
  return null;
}
